import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { GroupInviteDto } from '@mondy/shared';
import { api } from '@/api';

interface InviteResponse {
  invites: GroupInviteDto[];
}

/**
 * Owner-only: invite an email address to a group. Server emails the recipient
 * a link to `/accept_group_invite/:token`. Invalidates the group's invite
 * list on success.
 *
 * Distinct error codes to surface in the UI:
 *   - ALREADY_MEMBER        (409) — "This email is already in the group"
 *   - CANNOT_INVITE_SELF    (400) — "You're already a member of this group"
 *   - MAIL_NOT_CONFIGURED   (503) — "Email invitations are temporarily unavailable"
 *   - INVITE_EMAIL_FAILED   (502) — "Couldn't send the invitation"
 */
export function useInviteByEmail(groupId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      api.post<InviteResponse>(`/api/groups/${groupId}/invites`, { email }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['groups', 'invites', groupId] });
    },
  });
}
